import React from "react";
import { Card, Tag, Typography, Button, Space, Spin } from "antd";
import { EyeOutlined, EditOutlined } from "@ant-design/icons";
import { Draggable } from "@hello-pangea/dnd";
import type { GridColumnMetadata } from "../../types";
import { snakeToCamel } from "../../utils/fieldMapping";
import { getItemTitle } from "../../utils/itemTitle";

const { Text } = Typography;

export interface KanbanCardLabels {
  yes: string;
  no: string;
  view: string;
  edit: string;
  columnLabels: Record<string, string>;
}

interface KanbanCardProps {
  record: Record<string, unknown>;
  columns: GridColumnMetadata[];
  groupByField: string;
  listLabelLookup: Record<string, Record<string, string>>;
  index: number;
  locale: string;
  isPromoting?: boolean;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  labels: KanbanCardLabels;
}

/**
 * A single draggable card in the Kanban board.
 * Shows a title + 2-3 key fields + action buttons.
 *
 * Hooks (useGo, useTranslation) are lifted to KanbanBoard parent
 * to avoid per-card instantiation overhead.
 */
export const KanbanCard = React.memo(function KanbanCard({
  record,
  columns,
  groupByField,
  listLabelLookup,
  index,
  locale,
  isPromoting,
  onView,
  onEdit,
  labels,
}: KanbanCardProps) {
  const id = record.id != null ? String(record.id) : `__no-id-${index}__`;

  const title = getItemTitle(record) ?? `#${id?.slice(0, 8) ?? ""}`;

  // Display columns: first 3, excluding title column and grouping field
  const displayColumns = columns
    .filter((col) => {
      const camelName = snakeToCamel(col.name);
      if (camelName === groupByField) return false;
      if (
        col.name === "name" ||
        col.name === "item_number" ||
        col.name === "keyed_name"
      )
        return false;
      return true;
    })
    .slice(0, 3);

  const formatValue = (
    value: unknown,
    col: GridColumnMetadata,
  ): React.ReactNode => {
    if (value === null || value === undefined || value === "") {
      return <Text type="secondary">-</Text>;
    }

    // List type: show label from lookup
    if ((col.dataType === "list" || col.dataType === "filter list" || col.dataType === "mv_list") && col.dataSource) {
      const valueStr = String(value);
      const label = listLabelLookup[col.name]?.[valueStr] || valueStr;
      return <Tag>{label}</Tag>;
    }

    // Boolean
    if (col.dataType === "boolean") {
      return value ? labels.yes : labels.no;
    }

    // Date — use dynamic locale from i18n
    if (col.dataType === "date" || col.dataType === "datetime") {
      try {
        const date = new Date(String(value));
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString(locale);
        }
      } catch {
        // fall through
      }
    }

    return String(value);
  };

  return (
    <Draggable draggableId={String(id)} index={index}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          size="small"
          hoverable
          style={{
            marginBottom: 8,
            opacity: snapshot.isDragging ? 0.85 : isPromoting ? 0.6 : 1,
            boxShadow: snapshot.isDragging
              ? "0 4px 12px rgba(0,0,0,0.15)"
              : undefined,
            cursor: isPromoting ? "wait" : "grab",
            position: "relative",
            ...provided.draggableProps.style,
          }}
        >
          {isPromoting && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.5)",
                borderRadius: 6,
                zIndex: 1,
              }}
            >
              <Spin size="small" />
            </div>
          )}

          {/* Title */}
          <Text strong ellipsis style={{ display: "block", marginBottom: 4 }}>
            {title}
          </Text>

          {/* Fields */}
          {displayColumns.map((col) => {
            const value = record[snakeToCamel(col.name)];
            if (value === null || value === undefined) return null;
            return (
              <div key={col.name} style={{ marginBottom: 2 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {labels.columnLabels[col.name] ?? col.label}:{" "}
                </Text>
                <span style={{ fontSize: 13 }}>{formatValue(value, col)}</span>
              </div>
            );
          })}

          {/* Actions */}
          <Space
            size={4}
            style={{ marginTop: 6 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onView(id)}
              aria-label={labels.view}
            />
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(id)}
              aria-label={labels.edit}
            />
          </Space>
        </Card>
      )}
    </Draggable>
  );
});
